import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import type { CallStatus } from "../api/types";

/** Countries whose numbers are shown in local (national) format; everything else is international. */
const LOCAL_FORMAT_COUNTRIES = new Set(["US", "CA"]);

/** Turns a status code into readable text with a capital first letter: "IN_PROGRESS" → "In progress". */
export function statusText(code: string | null | undefined): string {
  if (!code) return "—";
  const words = code.replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Presents a stored E.164 number for display:
 *  - United States and Canada: local format, e.g. (201) 555-0123
 *  - every other country (including other +1 countries such as Jamaica): international, e.g. +44 20 7946 0000
 * A +1 number whose country can't be determined is treated as US/Canada.
 */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "Unknown";
  if (e164 === "anonymous") return "Anonymous";
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  const local = parsed.country ? LOCAL_FORMAT_COUNTRIES.has(parsed.country) : parsed.countryCallingCode === "1";
  return local ? parsed.formatNational() : parsed.formatInternational();
}

/** File size for people: "20 KB", "3.4 MB". */
export const formatSize = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return "—";
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0");
  return h ? `${h}:${mm}:${String(sec).padStart(2, "0")}` : `${mm}:${String(sec).padStart(2, "0")}`;
}

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
export const formatDateTime = (iso: string | null | undefined) => (iso ? dateTime.format(new Date(iso)) : "—");

export const STATUS_LABELS: Record<CallStatus, string> = {
  INITIATING: "Connecting",
  RINGING: "Ringing",
  CONNECTED: "Connected",
  ON_HOLD: "On hold",
  TRANSFERRING: "Transferring",
  COMPLETED: "Completed",
  FAILED: "Failed",
  BUSY: "Busy",
  NO_ANSWER: "No answer",
  CANCELED: "Canceled",
};

const END_REASONS: Record<string, string> = {
  CALLER_HANGUP: "Caller hung up",
  AGENT_HANGUP: "Agent hung up",
  REMOTE_HANGUP: "Other party hung up",
  DESTINATION_BUSY: "Destination busy",
  DESTINATION_NO_ANSWER: "Destination did not answer",
  REJECTED_BY_AGENTS: "Declined by agents",
  NO_AGENTS_AVAILABLE: "No agents available",
  RING_TIMEOUT: "Not answered in time",
  CALLER_ABANDONED: "Caller hung up before answer",
  CANCELED_BY_AGENT: "Canceled by agent",
  CLIENT_ABANDONED: "Browser never connected",
  TRANSFER_FAILED: "Transfer failed",
  PROVIDER_ERROR: "Telephony provider error",
};
export const endReasonLabel = (r: string | null | undefined) => (r ? (END_REASONS[r] ?? r) : null);

export function errorMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") return err.message;
  return "Something went wrong";
}
